import * as React from 'react';
import { useClient } from '../client';
import { getChildren, PropsWithOptionalFunctionalChildren } from '../../helpers/functionalChildren';
import { OfficeApi } from './OfficeApi';
import { BodyCoersionType } from './OfficeTypes';
import { OutlookAttachment, RawEmailData, StakeholderInformation } from '../client/types/CreateEmailObjectRequest';
import { useExistingStakeholders } from './useExistingStakeholders';
import { useNavigation } from '../routing';

interface EmailData {
  rawData: RawEmailData | null;
  recipients: Office.EmailAddressDetails[];
  attachments: OutlookAttachment[];
  subject: string;
}

type FormState = {
  selectedExistingStakeholders: Map<number | string, boolean>;
  selectedNewStakeholders: Map<number | string, boolean>;
  summary: string;
  includeBcc: boolean;
  selectedProjects: number[];
  selectedAttachments: Map<string, boolean>;
};

type SubmitResponse = { success: true; error: null } | { success: false; error: any };

export type ItemContextType = {
  emailObject: FormState;
  setFormValue: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  isValid: boolean;
  emailRecipients: Office.EmailAddressDetails[];
  submit: () => Promise<SubmitResponse>;
  cmStakeholders: StakeholderInformation[];
  newStakeholders: StakeholderInformation[];
  isLoadingStakeholders: boolean;
  attachments: OutlookAttachment[];
  showBcc: boolean;
  refetchStakeholders: () => void;
  getIncludedStakeholders: () => StakeholderInformation[];
  refetchEmailData: () => void;
};

const SUBJECT_POLLING_INTERVAL = 1000;

export const ItemContext = React.createContext<ItemContextType | null>(null);

const ItemProvider = ({ children }: PropsWithOptionalFunctionalChildren<unknown, ItemContextType>) => {
  const { pushEmail, checkSummary } = useClient();
  const { navigate } = useNavigation();

  const isSummaryEditedRef = React.useRef(false);

  const [emailObject, setEmailObject] = React.useState<FormState>({
    includeBcc: false,
    selectedExistingStakeholders: new Map(),
    selectedNewStakeholders: new Map(),
    summary: '',
    selectedProjects: [],
    selectedAttachments: new Map(),
  });

  const [emailData, setEmailData] = React.useState<EmailData>({
    rawData: null,
    recipients: [],
    attachments: [],
    subject: '',
  });

  const currentItemRef = React.useRef<string>('');
  const isMountedRef = React.useRef(true);

  const { cmStakeholders, newStakeholders, isLoading: isLoadingStakeholders, refetch, matchingUsers } = useExistingStakeholders(emailData.recipients);

  const updateSubject = React.useCallback(async () => {
    try {
      const itemId = await OfficeApi.getItemId();
      if (currentItemRef.current !== itemId || !isMountedRef.current) return;

      const subject = await OfficeApi.getSubject();
      if (!isSummaryEditedRef.current) {
        setEmailObject((prev) => {
          return prev.summary === subject
            ? prev
            : {
                ...prev,
                summary: subject,
              };
        });
      }
    } catch (error) {
      console.error('Error updating subject:', error);
    }
  }, []);

  const fetchEmailData = React.useCallback(async (): Promise<RawEmailData | undefined> => {
    if (!isMountedRef.current) return undefined;

    try {
      const itemId = await OfficeApi.getItemId();
      const isNewItem = itemId !== currentItemRef.current;

      if (isNewItem) {
        isSummaryEditedRef.current = false;
        navigate('main');
        currentItemRef.current = itemId;
      }

      const [bodyHtml, bodyText, subject, to, from, cc, bcc, attachments] = await Promise.all([
        OfficeApi.getBody(BodyCoersionType.Html),
        OfficeApi.getBody(BodyCoersionType.Text),
        OfficeApi.getSubject(),
        OfficeApi.getTo(),
        OfficeApi.getFrom(),
        OfficeApi.getCC(),
        OfficeApi.getBCC(),
        OfficeApi.getAttachments(),
      ]);

      let inlineAttachments = attachments.filter((a) => a.isInline);

      let cidPrefix = 'cid:';

      let doc = new DOMParser().parseFromString(bodyHtml, 'text/html');
      Array.from(doc.images)
        .filter((img) => Array.from(img.attributes).some((attr) => attr.value.includes(cidPrefix)))
        .forEach((img, index) => {
          let rawContentId = img.hasAttribute('originalSrc') ? img.getAttribute('originalSrc') : img.getAttribute('src');
          let matchingInlineAttachment = inlineAttachments[index];
          if (matchingInlineAttachment) matchingInlineAttachment.contentId = rawContentId.replace(cidPrefix, '');
        });

      if (!isMountedRef.current || currentItemRef.current !== itemId) return undefined;

      const rawData: RawEmailData = {
        to: to.map((r) => ({ email: r.emailAddress, name: r.displayName })),
        cc: cc.map((r) => ({ email: r.emailAddress, name: r.displayName })),
        bcc: bcc.map((r) => ({ email: r.emailAddress, name: r.displayName })),
        from: { email: from.emailAddress, name: from.displayName },
        subject,
        bodyText,
        bodyHtml,
        attachments,
        itemId,
      };

      let recipients = [from, ...to, ...cc];
      if (emailObject.includeBcc) {
        recipients = [...recipients, ...bcc];
      }

      setEmailData({
        rawData,
        recipients: recipients.filter(Boolean),
        attachments: attachments.filter((x) => !x.isInline),
        subject,
      });

      if (isNewItem || !isSummaryEditedRef.current) {
        setEmailObject((prev) => ({
          ...prev,
          summary: subject,
        }));
      }

      const summary = await checkSummary(rawData);
      if (summary && isMountedRef.current && currentItemRef.current === itemId) {
        navigate('summary', summary);
      }
      return rawData;
    } catch (error) {
      console.error('Error fetching email data:', error);
    }
    return undefined;
  }, [emailObject.includeBcc, checkSummary, navigate]);

  React.useEffect(() => {
    const intervalId = setInterval(updateSubject, SUBJECT_POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [updateSubject]);

  React.useEffect(() => {
    isMountedRef.current = true;

    const handleItemChanged = async () => {
      isSummaryEditedRef.current = false;
      navigate('main');
      await fetchEmailData();

      await updateSubject();
    };

    const handleRecipientsChanged = async (event: Office.RecipientsChangedEventArgs) => {
      if (!emailObject.includeBcc && !(event.changedRecipientFields.to || event.changedRecipientFields.cc)) {
        return;
      }
      await fetchEmailData();
    };

    fetchEmailData();
    OfficeApi.subscribeToItemChanged(handleItemChanged);
    OfficeApi.subscribeToRecipientsChanged(handleRecipientsChanged);
    OfficeApi.subscribeToAttachmentsChanged(() => fetchEmailData());

    return () => {
      isMountedRef.current = false;
      OfficeApi.unsubscribeFromItemChanged();
      OfficeApi.unsubscribeFromRecipientsChanged();
      OfficeApi.unsubscribeFromAttachmentsChanged();
    };
  }, [fetchEmailData, emailObject.includeBcc, navigate, updateSubject]);

  React.useEffect(() => {
    const updateStakeholderSelections = (
      stakeholders: StakeholderInformation[],
      prevSelected: Map<string | number, boolean>,
      keyExtractor: (obj: StakeholderInformation) => string | number,
    ) => {
      const updatedMap = new Map(
        [...prevSelected.entries()].filter(([key]) => stakeholders.some((s) => keyExtractor(s).toString() === key.toString())),
      );

      stakeholders.forEach((stakeholder) => {
        const key = keyExtractor(stakeholder);
        if (!updatedMap.has(key)) {
          updatedMap.set(key, true);
        }
      });

      return updatedMap;
    };

    setEmailObject((prev) => ({
      ...prev,
      selectedNewStakeholders: updateStakeholderSelections(newStakeholders, prev.selectedNewStakeholders, (stakeholder) => stakeholder.email),
      selectedExistingStakeholders: updateStakeholderSelections(cmStakeholders, prev.selectedExistingStakeholders, (stakeholder) => stakeholder.id),
    }));
  }, [cmStakeholders, newStakeholders]);

  React.useEffect(() => {
    setEmailObject((prev) => {
      const attachmentsMap = new Map(prev.selectedAttachments);
      for (const attachment of emailData.attachments) {
        if (!attachmentsMap.has(attachment.id)) {
          attachmentsMap.set(attachment.id, true);
        }
      }
      return { ...prev, selectedAttachments: attachmentsMap };
    });
  }, [emailData.attachments]);

  const setFormValue = React.useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    if (field === 'summary') {
      isSummaryEditedRef.current = true;
    }
    setEmailObject((prev) => ({ ...prev, [field]: value }));
  }, []);

  const getIncludedStakeholders: () => StakeholderInformation[] = () => {
    return [...newStakeholders, ...cmStakeholders].filter(
      (x) => !!emailObject.selectedExistingStakeholders.get(x.id) || !!emailObject.selectedNewStakeholders.get(x.email),
    );
  };

  const getIncludedUsers: () => StakeholderInformation[] = () => {
    return matchingUsers.filter((x) => !cmStakeholders.some((s) => s.email.toLowerCase() === x.email.toLowerCase()));
  };

  const submit = async () => {
    const rawData = await fetchEmailData();
    if (!rawData) return { success: false, error: 'No email data' };

    const selectedStakeholders = getIncludedStakeholders().map((s) => ({ ...s, stakeholderId: s.id }));
    const allUsers = getIncludedUsers();

    try {
      const summary = await pushEmail({
        summary: emailObject.summary,
        projectIds: emailObject.selectedProjects,
        attachments: emailData.attachments.filter((x) => emailObject.selectedAttachments.get(x.id)),
        recipients: [...allUsers, ...selectedStakeholders],
        raw: rawData,
      });

      if (isMountedRef.current) {
        navigate('summary', summary);
      }
      return { success: true, error: null };
    } catch (error) {
      return { error, success: false };
    }
  };

  const isValid = emailObject.selectedProjects.length > 0 && !!emailObject.summary;

  const value = {
    emailObject,
    setFormValue,
    isValid,
    emailRecipients: emailData.recipients,
    submit,
    newStakeholders,
    cmStakeholders,
    getIncludedStakeholders,
    attachments: emailData.attachments,
    isLoadingStakeholders,
    showBcc: !OfficeApi.isReadSurface() || emailData.rawData?.bcc.length > 0,
    refetchStakeholders: refetch,
    refetchEmailData: fetchEmailData,
  };

  return <ItemContext.Provider value={value}>{getChildren(children, value)}</ItemContext.Provider>;
};

export const useItem = () => {
  const context = React.useContext(ItemContext);
  if (!context) {
    throw new Error('useItem must be used within an ItemProvider');
  }
  return context;
};

export default ItemProvider;
